import { Injectable } from '@angular/core';
import { AbstractControl, ValidatorFn, Validators } from '@angular/forms';
import { ToastController } from '@ionic/angular';
import * as moment from 'moment';
import { Appointment } from 'src/app/core/models/appointment.model';
import { CustomerService } from 'src/app/core/models/customer-service.model';
import { EquipmentAttribute } from 'src/app/core/models/equipment-attribute.model';
import { InventoryAttribute, InventoryItem } from 'src/app/core/models/inventory.model';

@Injectable({
	providedIn: 'root'
})
export class InventoryFormHelperService {

	constructor(
		private toastController: ToastController
	) { }

	getDynamicAttributes(equipmentAttributes: EquipmentAttribute[], inventoryAttributes: InventoryAttribute[]): EquipmentAttribute[] {
		const secondaryInventoryAttributes = inventoryAttributes.slice(1);
		return secondaryInventoryAttributes.map(a => {
			const equipmentAttribute = equipmentAttributes.find(e => e.id === a.id);
			equipmentAttribute.dataType = equipmentAttribute.dataType.toLowerCase();
			equipmentAttribute.originalValue = a.value;
			equipmentAttribute.value = a.value;
			return equipmentAttribute;
		});
	}

	async buildDynamicForm(dynamicAttributes: EquipmentAttribute[], appointment: Appointment, serviceOptions: CustomerService[]) {
		const formFields = {};
		for (let index = 0; index < dynamicAttributes.length; index++) {
			const element = dynamicAttributes[index];
			formFields[index] = [element.value];

			if (element.dataType === 'customerservice') {
				if (!serviceOptions?.length) {
					serviceOptions = this.getServiceOptions(appointment);
				}

				if (element.value != null && element.value !== '' && !serviceOptions.some(s => s.id.toString() === element.value)) {
					serviceOptions.unshift(new CustomerService({ id: Number(element.value), desc: element.value }));
				}
			}

			const validators = this.getValidatorsByAttribute(element);
			formFields[index].push(Validators.compose(validators));
		}

		return { formFields, serviceOptions };
	}

	getValidatorsByAttribute(element: EquipmentAttribute) {
		const validators = element.isRequired ? [Validators.required] : [];
		const dataType = element.dataType?.toLowerCase();

		if (dataType === 'int') {
			validators.push(Validators.pattern('[0-9]+'));
			validators.push(this.numberMinLengthValidator(element.minLength));
			validators.push(this.numberMaxLengthValidator(element.maxLength));
		}

		if (dataType === 'char') {
			validators.push(Validators.pattern('[a-zA-Z]+'));
			validators.push(Validators.minLength(element.minLength));
			validators.push(Validators.maxLength(element.maxLength));
		}

		if (dataType === 'varchar') {
			validators.push(Validators.minLength(element.minLength));
			validators.push(Validators.maxLength(element.maxLength));
		}

		if (dataType === 'money') {
			validators.push(this.currencyValidator());
		}

		return validators;
	}

	updateInventoryDynamicAttributes(form: any, dynamicAttributes: EquipmentAttribute[], inventoryItem: InventoryItem): InventoryAttribute[] {
		const attributeUpdates = [] as InventoryAttribute[];
		for (let index = 0; index < dynamicAttributes.length; index++) {
			const attribute = dynamicAttributes[index];
			if (attribute.originalValue !== form[index]) {
				const value = form[index]?.toString();
				attributeUpdates.push({
					id: attribute.id,
					value
				});

				const attributeIndex = inventoryItem.attributes.findIndex(a => a.id == attribute.id);
				if (attributeIndex !== -1) {
					inventoryItem.attributes[attributeIndex].value = value;
				}
			}
		}
		return attributeUpdates;
	}

	async toastAddSuccess(): Promise<void> {
		const toast = await this.toastController.create({
			message: 'Successfully added inventory.',
			duration: 3000,
			position: 'top',
			color: 'success',
			buttons: ['Dismiss'],
		});

		await toast.present();
	}

	currencyValidator(): ValidatorFn {
		return (control: AbstractControl): { [key: string]: any } | null => {
			if (control.value == null) {
				return null;
			}

			const pattern = /^\d+\.?[\d]{0,2}$/;
			return !pattern.test(control.value) ? { moneyError: { value: control.value } } : null;
		};
	}

	getServiceOptions(appointment: Appointment): CustomerService[] {
		const services = appointment.services;
		services.sort((a, b) => a.desc > b.desc ? 1 : -1);
		return services;
	}

	numberMinLengthValidator(minLength: number): ValidatorFn {
		return (control: AbstractControl): { [key: string]: any } | null => {
			if (control.value == null) {
				return null;
			}

			return control.value.toString().length < minLength ? { minLengthError: { value: control.value } } : null;
		};
	}

	numberMaxLengthValidator(maxLength: number): ValidatorFn {
		return (control: AbstractControl): { [key: string]: any } | null => {
			if (control.value == null) {
				return null;
			}

			return control.value.toString().length > maxLength ? { maxLengthError: { value: control.value } } : null;
		};
	}

	preventNonAlphaCharacter(event: any) {
		const pattern = /[a-zA-Z]/;
		const inputChar = String.fromCharCode(event.charCode);

		if (!pattern.test(inputChar)) {
			// invalid character, prevent input
			event.preventDefault();
		}
	}

	preventNonInteger(event: any) {
		const pattern = /[0-9]/;
		const inputChar = String.fromCharCode(event.charCode);

		if (!pattern.test(inputChar)) {
			// invalid character, prevent input
			event.preventDefault();
		}
	}

	preventNonCurrency(event: any) {
		const pattern = /[0-9.]/;
		const inputChar = String.fromCharCode(event.charCode);

		// Prevent more than one decimal
		if (inputChar == '.' && event.target.value.includes('.')) {
			event.preventDefault();
		}

		if (!pattern.test(inputChar)) {
			// invalid character, prevent input
			event.preventDefault();
		}
	}

	formatMoneyInput($event) {
		let value: string = $event.target.value;
		if (value != null && value.length > 0) {
			if (value.indexOf('.') === -1) {
				value = `${value}.00`;
			}

			if (value.startsWith('.')) {
				value = `0${value}`;
			}

			const decimalPortion = value.split('.')[1];
			if (decimalPortion.length !== 2) {
				value = `${value}`.concat('0'.repeat(2 - decimalPortion.length));
			}

			$event.target.value = value;
		}
	}

	formatDateInput($event) {
		$event.target.value = moment($event.target.value).format('MMM DD YYYY HH:mm');
	}

	serviceCompareWith(o1, o2): boolean {
		return o1 && o2 ? o1.toString() === o2.toString() : o1 === o2;
	}

	toDateTimeString(inventoryDateString: string) {
		if (inventoryDateString == null) {
			return moment().format();
		}
		else {
			return moment(inventoryDateString, 'MMM DD YYYY HH:mm').format();
		}
	}

	toInventoryDateString(dateTimeString: string) {
		return moment(dateTimeString).format('MMM DD YYYY HH:mm');
    }
}
